import React, { forwardRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import useSWR, { mutate } from 'swr'
import * as UIcons from '@iconscout/react-unicons'
import Btn from 'components/Btn'
import ClickAwayComponent from 'components/ClickAwayComponent'
import useMerchant from 'hooks/useMerchant'
import PageHeader from '../shared/PageHeader'
import SummaryCard from '../shared/SummaryCard'
import MiniTable from '../shared/MiniTable'
import StatusPill from '../shared/StatusPill'
import BranchesSummary from './components/BranchesSummary'
import POSSummary from './components/POSSummary'
import EditBusinessInfo from './components/EditBusinessInfo'
import AddBranch from '../Branches/components/AddBranch'
import AddPOS from '../POSManagment/components/AddPOS'

export default withTranslation()(function BusinessOrganization({ t }) {
  const navigate = useNavigate()
  const { merchant } = useMerchant()
  const { data: branchesRes } = useSWR('merchants/branches')
  const { data: posRes } = useSWR('pos')
  const { data: contactsRes } = useSWR('merchants/contacts')
  const [showEditInfo, setShowEditInfo] = useState(false)
  const [showAddBranch, setShowAddBranch] = useState(false)
  const [showAddPOS, setShowAddPOS] = useState(false)

  const branches = branchesRes?.data ?? []
  const posDevices = posRes?.data ?? []
  const contacts = contactsRes?.data ?? []
  const primaryContact = contacts[0]
  const branchNameById = branches.reduce((acc, branch) => ({ ...acc, [branch.id]: branch.name }), {})
  const category = merchant?.category ? JSON.parse(merchant.category) : null

  // merchant_id/registration_number/tax_id/currency are read optimistically —
  // they aren't part of the current merchant hook's confirmed fields, so a
  // row only renders when the API actually returns a value for it.
  const businessInfoRows = [
    { label: t('settings.business_organization.fields.name'), value: merchant?.name },
    { label: t('settings.business_organization.fields.merchant_id'), value: merchant?.merchant_id },
    { label: t('settings.business_organization.fields.registration_number'), value: merchant?.registration_number },
    { label: t('settings.business_organization.fields.category'), value: category?.name },
    { label: t('settings.business_organization.fields.tax_id'), value: merchant?.tax_id },
    { label: t('settings.business_organization.fields.country'), value: merchant?.head_office_country },
    { label: t('settings.business_organization.fields.currency'), value: merchant?.currency },
    { label: t('settings.business_organization.fields.address'), value: merchant?.head_office_address },
    { label: t('settings.business_organization.fields.website'), value: merchant?.website }
  ].filter(({ value }) => value)

  const menuActions = [
    {
      key: 'edit-business',
      icon: UIcons.UilPen,
      label: t('settings.business_organization.quick_actions.edit_business.title'),
      onClick: () => setShowEditInfo(true)
    },
    {
      key: 'edit-contact',
      icon: UIcons.UilUserPlus,
      label: t('settings.business_organization.quick_actions.edit_contact.title'),
      onClick: () => navigate('/settings/contact-person')
    },
    {
      key: 'create-branch',
      icon: UIcons.UilBuilding,
      label: t('settings.business_organization.quick_actions.create_branch.title'),
      onClick: () => setShowAddBranch(true)
    },
    {
      key: 'add-pos',
      icon: UIcons.UilCalculator,
      label: t('settings.business_organization.quick_actions.add_pos.title'),
      onClick: () => setShowAddPOS(true)
    }
  ]

  const linkAction = (label, url) => (
    <Btn
      content={() => <span className="flex items-center gap-1.5">{label} <UIcons.UilArrowRight size={14} /></span>}
      className="text-dark-blue border border-border-lines rounded-full h-9 px-4 whitespace-nowrap font-medium"
      click={() => navigate(url)}
    />
  )

  return (
    <section className="w-full">
      <PageHeader
        crumbs={[
          { name: t('settings.hub.title'), url: '/settings' },
          { name: t('settings.business_organization.title') }
        ]}
        title={t('settings.business_organization.title')}
        description={t('settings.business_organization.description')}
        right={
          <div className="flex items-center gap-2">
            {merchant?.status && <StatusPill active={merchant.status === 'active'} />}

            <Btn
              content={() => <span className="flex items-center gap-1.5"><UIcons.UilPen size={14} /> {t('misc.edit')}</span>}
              className="text-dark-blue bg-white border border-border-lines rounded-full h-9 px-4 font-medium"
              click={() => setShowEditInfo(true)}
            />

            <ClickAwayComponent
              Component={forwardRef(({ handleClick, open }, ref) => (
                <div className="relative" ref={ref}>
                  <button
                    type="button"
                    onClick={handleClick}
                    className="w-9 h-9 rounded-full border border-border-lines flex items-center justify-center hover:bg-background"
                  >
                    <UIcons.UilEllipsisV className="text-secondary" size={18} />
                  </button>

                  {
                    open && (
                      <ul className="absolute right-0 top-11 z-20 w-56 bg-white border border-border-lines rounded-xl shadow-lg py-2 divide-y divide-dashed divide-border-lines">
                        {
                          menuActions.map(({ key, icon: Icon, label, onClick }) => (
                            <li key={key}>
                              <button
                                type="button"
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-dark-blue hover:bg-background text-left"
                                onClick={() => { handleClick(); onClick() }}
                              >
                                <Icon size={16} className="text-secondary" /> {label}
                              </button>
                            </li>
                          ))
                        }
                      </ul>
                    )
                  }
                </div>
              ))}
            />
          </div>
        }
      />

      <div className="flex flex-col gap-3 mb-5">
        <SummaryCard icon={UIcons.UilBuilding} title={t('settings.business_organization.business_info.title')}>
          <div className="flex flex-col md:flex-row gap-10">
            {
              merchant?.merchant_avatar && (
                <img src={merchant.merchant_avatar} alt="" className="w-24 h-24 rounded-2xl object-cover border border-border-lines shrink-0" />
              )
            }

            <div className="grid grid-cols-[auto,1fr] gap-y-2 gap-x-6 w-full">
              {
                businessInfoRows.map(({ label, value }) => (
                  <React.Fragment key={label}>
                    <span className="text-base text-secondary whitespace-nowrap">{label}</span>
                    <span className="text-base text-dark-blue">{value}</span>
                  </React.Fragment>
                ))
              }
            </div>
          </div>
        </SummaryCard>

        <SummaryCard
          icon={UIcons.UilUsersAlt}
          title={t('settings.business_organization.contact_person.title')}
          action={linkAction(t('settings.business_organization.contact_person.manage'), '/settings/contact-person')}
        >
          <MiniTable
            columns={[
              { key: 'name', label: t('settings.business_organization.contact_person.columns.name') },
              { key: 'type', label: t('settings.business_organization.contact_person.columns.type') },
              { key: 'phone', label: t('settings.business_organization.contact_person.columns.phone') },
              { key: 'email', label: t('settings.business_organization.contact_person.columns.email') },
              { key: 'status', label: t('misc.status') }
            ]}
            rows={
              primaryContact ? [{
                id: primaryContact.id,
                name: `${primaryContact.first_name ?? ''} ${primaryContact.last_name ?? ''}`,
                type: primaryContact.contact_type
                  ? t(`settings.business_organization.contact_person.types.${primaryContact.contact_type}`)
                  : t('settings.business_organization.contact_person.primary_contact'),
                phone: primaryContact.phone,
                email: primaryContact.email,
                status: primaryContact.status ?? 'active'
              }] : []
            }
            emptyTitle={t('settings.business_organization.contact_person.empty')}
            emptyHint={t('settings.business_organization.contact_person.empty_hint')}
          />
        </SummaryCard>

        <SummaryCard
          icon={UIcons.UilStore}
          title={t('settings.business_organization.branches.title')}
          action={linkAction(t('settings.business_organization.view_branches'), '/settings/branches')}
        >
          <BranchesSummary />

          <MiniTable
            columns={[
              { key: 'name', label: t('settings.branch.table.header.name') },
              { key: 'location', label: t('settings.branch.table.header.location') },
              { key: 'status', label: t('misc.status') }
            ]}
            rows={branches.slice(0, 4)}
            moreCount={Math.max(0, branches.length - 4)}
            moreUrl="/settings/branches"
            moreLabel={t('settings.business_organization.more_branches')}
            emptyTitle={t('settings.business_organization.branches.empty')}
            emptyHint={t('settings.business_organization.branches.empty_hint')}
          />
        </SummaryCard>

        <SummaryCard
          icon={UIcons.UilCalculator}
          title={t('settings.business_organization.pos.title')}
          action={linkAction(t('settings.business_organization.view_pos'), '/settings/pos-management')}
        >
          <POSSummary />

          <MiniTable
            columns={[
              { key: 'name', label: t('pos.form.name.label') },
              { key: 'branch', label: t('pos.name') },
              { key: 'status', label: t('misc.status') }
            ]}
            rows={posDevices.slice(0, 4).map((device) => ({ ...device, branch: branchNameById[device.branch_id] ?? '—' }))}
            moreCount={Math.max(0, posDevices.length - 4)}
            moreUrl="/settings/pos-management"
            moreLabel={t('settings.business_organization.more_pos')}
            emptyTitle={t('settings.business_organization.pos.empty')}
            emptyHint={t('settings.business_organization.pos.empty_hint')}
          />
        </SummaryCard>
      </div>

      {showEditInfo && <EditBusinessInfo info={merchant} handleClick={() => setShowEditInfo(false)} />}
      {showAddBranch && <AddBranch handleClick={() => setShowAddBranch(false)} onSaved={() => mutate('merchants/branches')} />}
      {showAddPOS && <AddPOS handleClick={() => setShowAddPOS(false)} onSaved={() => mutate('pos')} />}
    </section>
  )
})
